import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { auth, authApi } from '@/services/api';
import { usePageContent } from '@/hooks/usePageContent';

function stripHtml(html: string): string {
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

export default function MembersLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [tenantId, setTenantId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const { getContent } = usePageContent('members');
  const brandTagline = stripHtml(getContent('members-brand-tagline')) || 'CashMyProperty.ai — Property owner & tenant portal';
  const companyIdLabel = stripHtml(getContent('members-company-id-label')) || 'Company ID';
  const emailLabel = stripHtml(getContent('members-email-label')) || 'Email Address';
  const passwordLabel = stripHtml(getContent('members-password-label')) || 'Password';
  const rememberLabel = stripHtml(getContent('members-remember-label')) || 'Remember me';
  const forgotLabel = stripHtml(getContent('members-forgot-label')) || 'Forgot password?';
  const submitLabel = stripHtml(getContent('members-submit-label')) || 'Sign In to My Portal';
  const staffLink = stripHtml(getContent('members-staff-link')) || 'Are you staff? Staff login';

  if (auth.isAuthenticated()) {
    const role = auth.getRole();
    const memberRoles = ['landowner', 'tenant', 'viewer'];
    if (memberRoles.includes(role || '')) {
      return <Navigate to="/members/portal" replace />;
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email.trim() || !password.trim() || !tenantId.trim()) {
      setError('All fields are required');
      return;
    }

    const tid = parseInt(tenantId, 10);
    if (isNaN(tid) || tid < 1) {
      setError('Company ID must be a valid number');
      return;
    }

    setLoading(true);
    try {
      const res = await authApi.login(email.trim(), password, tid);
      if (res.data) {
        const data = res.data as { token: string; tenant_id: number; user: { role: string } };

        // Only allow member roles on /members
        const memberRoles = ['landowner', 'tenant', 'viewer'];
        if (!memberRoles.includes(data.user.role)) {
          setError('This portal is for property owners and tenants. Please use the staff login.');
          setLoading(false);
          return;
        }

        auth.setToken(data.token);
        auth.setTenantId(data.tenant_id);
        auth.setRole(data.user.role);
        window.dispatchEvent(new Event('storage'));
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Login failed. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-50 px-4 relative overflow-hidden">
      {/* Background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] rounded-full bg-primary-500/5 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full bg-accent-500/5 blur-3xl pointer-events-none" />

      <div className="w-full max-w-md relative z-10">
        {/* Brand */}
        <div className="text-center mb-8">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20minimalist%20geometric%20logo%20mark%20for%20premium%20real%20estate%20brand%2C%20rich%20mustard%20gold%20and%20dark%20charcoal%20tones%2C%20clean%20elegant%20modern%20corporate%20design%2C%20gold%20and%20ivory%20palette%2C%20professional%20premium%20luxury%20identity%20mark%2C%20pure%20white%20background%2C%20highly%20detailed%20vector%20style%20flat%20icon&width=200&height=200&seq=brand-mark-members-gold-01&orientation=squarish&nocache=true"
            alt="CashMyProperty"
            className="w-16 h-16 rounded-2xl object-cover mx-auto mb-5"
          />
          <div className="flex items-center justify-center gap-1 mb-1">
            <h1 className="font-heading text-2xl font-bold text-foreground-950">CashMyProperty</h1>
            <span className="font-heading text-2xl font-bold text-primary-600">.ai</span>
          </div>
          <p className="text-sm text-foreground-600">{brandTagline}</p>
        </div>

        {/* Form card */}
        <form
          onSubmit={handleSubmit}
          className="bg-background-50 border border-background-200 rounded-2xl p-8 space-y-5 shadow-sm"
        >
          {error && (
            <div className="flex items-start gap-3 p-3 rounded-xl bg-rose-500/10 border border-rose-500/25">
              <i className="ri-error-warning-line text-rose-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-rose-600">{error}</p>
            </div>
          )}

          <div>
            <label htmlFor="tenant_id" className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-1.5">
              {companyIdLabel}
            </label>
            <div className="relative">
              <i className="ri-building-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                id="tenant_id"
                type="text"
                inputMode="numeric"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                placeholder="e.g. 1"
                autoComplete="off"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-100 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors"
              />
            </div>
          </div>

          <div>
            <label htmlFor="email" className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-1.5">
              {emailLabel}
            </label>
            <div className="relative">
              <i className="ri-mail-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                autoComplete="email"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-100 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors"
              />
            </div>
          </div>

          <div>
            <label htmlFor="password" className="block text-xs font-semibold text-foreground-600 uppercase tracking-wider mb-1.5">
              {passwordLabel}
            </label>
            <div className="relative">
              <i className="ri-lock-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                className="w-full pl-10 pr-4 py-3 rounded-xl bg-background-100 border border-background-300 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-500/15 transition-colors"
              />
            </div>
          </div>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-background-300 bg-background-100 text-primary-500 focus:ring-primary-500/20"
              />
              <span className="text-xs text-foreground-600">Remember me</span>
            </label>
            <button type="button" className="text-xs text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer">
              Forgot password?
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl bg-primary-500 text-white font-semibold text-sm hover:bg-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <i className="ri-login-box-line" />
                {submitLabel}
              </>
            )}
          </button>
        </form>

        <div className="flex items-start gap-2.5 p-3.5 rounded-xl border border-accent-200/60 bg-accent-50/40 mt-5">
          <i className="ri-moon-line text-accent-700 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-accent-900 leading-relaxed">
            All sessions are automatically logged out daily at 00:00. You&apos;ll simply need to sign in again.
          </p>
        </div>

        <p className="text-center text-xs text-foreground-600 mt-6">
          Are you staff? <a href="/admin" className="text-primary-600 hover:text-primary-700 transition-colors">Staff login</a>
        </p>
      </div>
    </div>
  );
}